(function (global) {
  'use strict';

  const CONFIG = Object.freeze({
    DEFAULT_BVD_MM: 12,
    DISPLAY_STEP_D: 0.25,
    SPHERE_MIN_D: -30,
    SPHERE_MAX_D: 30,
    CYLINDER_MIN_D: -10,
    CYLINDER_MAX_D: 10
  });

  const roundToStep = (value, step = CONFIG.DISPLAY_STEP_D) => {
    if (!Number.isFinite(value)) return null;
    const rounded = Math.sign(value || 1) * Math.round((Math.abs(value) / step) + Number.EPSILON) * step;
    return Math.abs(rounded) < 0.000001 ? 0 : rounded;
  };

  const vertex = (power, bvdMm = CONFIG.DEFAULT_BVD_MM) => {
    if (!Number.isFinite(power) || !Number.isFinite(bvdMm)) return null;
    const denominator = 1 - ((bvdMm / 1000) * power);
    return Math.abs(denominator) < 0.000001 ? null : power / denominator;
  };

  const normalizeAxis = (axis) => {
    const numeric = Number(axis);
    if (!Number.isFinite(numeric)) return null;
    const wrapped = ((numeric - 1) % 180 + 180) % 180 + 1;
    return wrapped;
  };

  const normalizeMinusCylinder = ({ sphere, cylinder = 0, axis = null }) => {
    let normalizedSphere = Number(sphere);
    let normalizedCylinder = Number(cylinder || 0);
    let normalizedAxis = normalizedCylinder === 0 ? null : normalizeAxis(axis);
    if (normalizedCylinder > 0) {
      normalizedSphere += normalizedCylinder;
      normalizedCylinder = -normalizedCylinder;
      normalizedAxis = normalizeAxis((normalizedAxis || 0) + 90);
    }
    return { sphere: normalizedSphere, cylinder: normalizedCylinder, axis: normalizedAxis };
  };

  const astigmatismBand = (cylinder) => {
    const magnitude = Math.abs(Number(cylinder) || 0);
    if (magnitude <= 0.5) return 'low';
    if (magnitude <= 1.25) return 'mild';
    if (magnitude <= 2) return 'moderate';
    return 'high';
  };

  const isQuarterStep = (value) => Number.isFinite(value) && Math.abs((value * 4) - Math.round(value * 4)) < 0.000001;

  const POWER_MATCH = Object.freeze({ EXACT: 'exact', RANGE: 'range_only', NONE: 'none' });
  const POWER_TOLERANCE_D = 0.0001;
  const nearlyEqual = (left, right, tolerance = POWER_TOLERANCE_D) => Number.isFinite(Number(left))
    && Number.isFinite(Number(right))
    && Math.abs(Number(left) - Number(right)) <= tolerance;
  const powerAllowedByAvailability = (product, power, exactData) => {
    if (nearlyEqual(power, 0)) return exactData ? product.planoAvailable !== false : product.planoAvailable === true;
    return exactData ? product.prescriptionAvailable !== false : product.prescriptionAvailable === true;
  };
  const matchesPowerStepRule = (rule, power) => {
    if (!rule || !Number.isFinite(power)) return false;
    const min = Number(rule.min);
    const max = Number(rule.max);
    const step = Math.abs(Number(rule.step));
    if (![min, max, step].every(Number.isFinite) || step <= 0) return false;
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);
    if (power < lower - POWER_TOLERANCE_D || power > upper + POWER_TOLERANCE_D) return false;
    const anchor = Number.isFinite(Number(rule.anchor)) ? Number(rule.anchor) : lower;
    return nearlyEqual((power - anchor) / step, Math.round((power - anchor) / step));
  };
  const productPowerMatch = (product, power) => {
    if (!product || !Number.isFinite(power)) return { type: POWER_MATCH.NONE, matched: false, source: 'invalid' };
    const exactValues = Array.isArray(product.sphereValues)
      ? product.sphereValues.map(Number).filter(Number.isFinite)
      : [];
    if (exactValues.length) {
      const matched = powerAllowedByAvailability(product, power, true) && exactValues.some((value) => nearlyEqual(value, power));
      return { type: matched ? POWER_MATCH.EXACT : POWER_MATCH.NONE, matched, source: 'exact_values' };
    }
    const stepRules = Array.isArray(product.powerSteps) ? product.powerSteps : [];
    if (stepRules.length) {
      const matched = powerAllowedByAvailability(product, power, true) && stepRules.some((rule) => matchesPowerStepRule(rule, power));
      return { type: matched ? POWER_MATCH.EXACT : POWER_MATCH.NONE, matched, source: 'step_rules' };
    }
    if (!Number.isFinite(Number(product.minPower)) || !Number.isFinite(Number(product.maxPower))) {
      return { type: POWER_MATCH.NONE, matched: false, source: 'incomplete' };
    }
    const min = Math.min(Number(product.minPower), Number(product.maxPower));
    const max = Math.max(Number(product.minPower), Number(product.maxPower));
    const matched = powerAllowedByAvailability(product, power, false)
      && power >= min - POWER_TOLERANCE_D
      && power <= max + POWER_TOLERANCE_D;
    return { type: matched ? POWER_MATCH.RANGE : POWER_MATCH.NONE, matched, source: 'range' };
  };
  const productSupportsEstimate = (product, power) => productPowerMatch(product, power).matched;

  const BAND_CONTENT = Object.freeze({
    low: {
      label: 'Low astigmatism',
      message: 'A spherical contact lens may provide acceptable vision for many wearers at this level of astigmatism.',
      support: 'Your estimated spherical starting power is shown below. A contact lens fitting is still needed to confirm vision, comfort and fit.'
    },
    mild: {
      label: 'Mild astigmatism',
      message: 'A spherical contact lens can sometimes be worn at this level, but vision may be mildly blurred because the astigmatism correction is being omitted.',
      support: 'Consider toric lenses and an optometrist review before ordering.'
    },
    moderate: {
      label: 'Moderate astigmatism',
      message: 'A spherical contact lens may leave noticeable or moderate blur at this level of astigmatism because the cylinder correction is not being corrected.',
      support: 'The spherical estimate is educational only. Toric contact lenses and an optometrist review should be considered.'
    },
    high: {
      label: 'High astigmatism',
      message: 'A spherical contact lens would omit a substantial amount of cylinder correction at this level of astigmatism, so the toric shopping pathway is shown first.',
      support: 'View toric contact lenses or ask our optometry team. The mathematical spherical equivalent is available only in the calculation details.'
    }
  });

  const validateEye = (input) => {
    const sphereMissing = !input || input.sphere === '' || input.sphere === null || typeof input.sphere === 'undefined';
    const cylinderMissing = !input || input.cylinder === '' || input.cylinder === null || typeof input.cylinder === 'undefined';
    const axisMissing = !input || input.axis === '' || input.axis === null || typeof input.axis === 'undefined';
    if (sphereMissing && cylinderMissing && axisMissing) {
      return { empty: true, errors: [] };
    }
    const sphere = sphereMissing ? NaN : Number(input.sphere);
    const cylinder = input.cylinder === '' || input.cylinder === null || typeof input.cylinder === 'undefined' ? 0 : Number(input.cylinder);
    const axis = axisMissing ? null : Number(input.axis);
    const errors = [];
    if (!Number.isFinite(sphere) || sphere < CONFIG.SPHERE_MIN_D || sphere > CONFIG.SPHERE_MAX_D) errors.push('Enter a sphere between -30.00 and +30.00 D.');
    else if (!isQuarterStep(sphere)) errors.push('Enter power in 0.25 D steps.');
    if (!Number.isFinite(cylinder) || cylinder < CONFIG.CYLINDER_MIN_D || cylinder > CONFIG.CYLINDER_MAX_D) errors.push('Enter a cylinder between -10.00 and +10.00 D.');
    else if (!isQuarterStep(cylinder)) errors.push('Enter cylinder in 0.25 D steps.');
    if (Number.isFinite(cylinder) && cylinder !== 0 && (axisMissing || !Number.isInteger(axis) || axis < 1 || axis > 180)) errors.push('Enter a whole-number axis from 1 to 180 when cylinder is not 0.00.');
    if (Number.isFinite(cylinder) && cylinder === 0 && !axisMissing && (!Number.isInteger(axis) || axis < 1 || axis > 180)) errors.push('Axis must be a whole number from 1 to 180.');
    return { empty: false, errors, values: { sphere, cylinder, axis } };
  };

  const calculateEye = (input, bvdMm = CONFIG.DEFAULT_BVD_MM) => {
    const validation = validateEye(input);
    if (validation.empty || validation.errors.length) return { ...validation, valid: false };
    const normalized = normalizeMinusCylinder(validation.values);
    const sphericalEquivalent = normalized.sphere + (normalized.cylinder / 2);
    const sphericalUnrounded = vertex(sphericalEquivalent, bvdMm);
    const meridian1 = vertex(normalized.sphere, bvdMm);
    const meridian2 = vertex(normalized.sphere + normalized.cylinder, bvdMm);
    const band = astigmatismBand(normalized.cylinder);
    return {
      valid: true,
      empty: false,
      input: validation.values,
      normalized,
      sphericalEquivalent,
      sphericalUnrounded,
      sphericalDisplay: roundToStep(sphericalUnrounded),
      toric: {
        sphereUnrounded: meridian1,
        cylinderUnrounded: meridian2 - meridian1,
        sphereDisplay: roundToStep(meridian1),
        cylinderDisplay: roundToStep(meridian2 - meridian1),
        axis: normalized.axis
      },
      band,
      guidance: BAND_CONTENT[band]
    };
  };

  const calculateContactLensEye = (input) => {
    const validation = validateEye(input);
    if (validation.empty || validation.errors.length) return { ...validation, valid: false };
    const normalized = normalizeMinusCylinder(validation.values);
    const sphericalEquivalent = normalized.sphere + (normalized.cylinder / 2);
    const band = astigmatismBand(normalized.cylinder);
    return {
      valid: true,
      empty: false,
      input: validation.values,
      normalized,
      sphericalEquivalent,
      sphericalDisplay: roundToStep(sphericalEquivalent),
      band,
      guidance: BAND_CONTENT[band]
    };
  };

  const formatPower = (value, suffix = true) => {
    if (!Number.isFinite(value)) return '—';
    const text = `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
    return suffix ? `${text} D` : text;
  };

  const formatPrescription = (rx) => {
    if (!rx) return '—';
    const cylinder = Number(rx.cylinder || 0);
    const base = `${formatPower(Number(rx.sphere), false)} / ${formatPower(cylinder, false)}`;
    return cylinder === 0 ? base : `${base} × ${rx.axis}`;
  };

  const api = Object.freeze({ CONFIG, POWER_MATCH, BAND_CONTENT, roundToStep, vertex, normalizeAxis, normalizeMinusCylinder, astigmatismBand, isQuarterStep, nearlyEqual, matchesPowerStepRule, productPowerMatch, productSupportsEstimate, validateEye, calculateEye, calculateContactLensEye, formatPower, formatPrescription });
  global.CityOpticsContactLensConverter = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);

